import { useEffect, useState } from "react";
import { PublicLayout } from "@/components/site/PublicLayout";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { api } from "@/lib/api";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
    const [info, setInfo] = useState(null);
    const [form, setForm] = useState({ name: "", mobile: "", email: "", subject: "", message: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => { api.get("/contact").then((r) => setInfo(r.data)); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.mobile || !form.subject || !form.message) {
            toast.error("Please fill in all fields");
            return;
        }
        setBusy(true);
        try {
            await api.post("/contact/submit", form);
            toast.success("Thank you! We've received your message.");
            setForm({ name: "", mobile: "", email: "", subject: "", message: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <PublicLayout>
            <section className="container-x pt-10">
                <Breadcrumbs items={[{ label: "Contact" }]} />
                <div className="mt-5">
                    <div className="overline">Say Hello</div>
                    <h1 className="mt-3 font-serif text-5xl text-slate-900 sm:text-6xl">Contact Us</h1>
                    <p className="mt-4 max-w-2xl text-lg text-slate-600">
                        Reach out for grievances, memberships, tenders, media enquiries or just to say hello.
                    </p>
                </div>
            </section>

            <section className="container-x mt-12 pb-20">
                <div className="grid gap-10 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <div className="surface-card p-7">
                            <div className="overline">Office</div>
                            <ul className="mt-5 space-y-4 text-slate-600">
                                <li className="flex gap-3"><MapPin size={18} className="mt-1 text-brand-orange" />{info?.address}</li>
                                <li className="flex gap-3"><Mail size={18} className="mt-1 text-brand-orange" /><a className="hover:text-brand-blue" href={`mailto:${info?.email || ""}`}>{info?.email}</a></li>
                                {info?.phone && <li className="flex gap-3"><Phone size={18} className="mt-1 text-brand-orange" />{info.phone}</li>}
                                {info?.office_timing && <li className="flex gap-3"><Clock size={18} className="mt-1 text-brand-orange" />{info.office_timing}</li>}
                            </ul>
                        </div>
                        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
                            {info?.map_embed_url && (
                                <iframe title="Map" src={info.map_embed_url} className="h-72 w-full" loading="lazy" />
                            )}
                        </div>
                    </div>

                    <form onSubmit={submit} className="surface-card md:col-span-7 p-7" data-testid="contact-form">
                        <div className="overline">Message</div>
                        <h2 className="mt-3 font-serif text-3xl text-slate-900">Send us a message</h2>
                        <div className="mt-6 grid gap-4 sm:grid-cols-2">
                            <Field label="Name" value={form.name} onChange={set("name")} testid="contact-name" />
                            <Field label="Mobile" value={form.mobile} onChange={set("mobile")} testid="contact-mobile" />
                            <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="contact-email" />
                            <Field label="Subject" value={form.subject} onChange={set("subject")} testid="contact-subject" />
                        </div>
                        <label className="mt-4 block text-xs font-medium uppercase tracking-wider text-slate-500">Message</label>
                        <textarea
                            value={form.message}
                            onChange={set("message")}
                            rows={5}
                            data-testid="contact-message"
                            className="mt-1 w-full rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
                            placeholder="How can we help?"
                        />
                        <button type="submit" disabled={busy} className="btn-accent mt-6" data-testid="contact-submit">
                            {busy ? "Sending…" : (<>Send Message <Send size={14} /></>)}
                        </button>
                    </form>
                </div>
            </section>
        </PublicLayout>
    );
}

const Field = ({ label, testid, ...props }) => (
    <label className="block">
        <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</span>
        <input
            {...props}
            data-testid={testid}
            className="mt-1 w-full rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
        />
    </label>
);
